import { client } from "../supabase";

/**
 * PAGOS CapEx candidatos a asociarse a una factura recibida.
 *
 * Contexto: se pagan trabajos de CapEx a un proveedor ANTES de que exista la factura (adelantos
 * por el modal "Inversión" del edificio → path CapEx caja-pura de VincularMovimientoModal). Cada
 * pago es UNA fila `movimientos_caja` con `categoria_pago='compra_activo'`, `entidad_tipo='otro'`,
 * `activo_id=null`; el "quién pagó qué" vive en `plan_pagos_activos.movimiento_bancario_id`
 * (cuota→banco). Cuando la factura llega, el usuario asocia esos pagos a la factura (Fase 2).
 *
 * Este util SOLO lista pagos HUÉRFANOS (todavía sin factura), con doble candado para no listar ni
 * tocar jamás un pago ya vinculado:
 *   (1) MBF del pago vacío: su `movimiento_bancario_id` NO tiene fila en `movimiento_bancario_facturas`
 *       (Σ MBF = 0). El MBF es la única fuente de verdad de "factura de compra pagada"
 *       (documentosCosto.derivarPago) — si existe, el pago ya está atado a una factura.
 *   (2) Activos candidatos: TODOS los activos que paga el bancario (vía cuotas pagadas) cumplen
 *       `estado='planificado'` + `factura_sii_recibida_id IS NULL` + `eliminado=false` + TODAS sus
 *       cuotas de `plan_pagos_activos` en `estado='pagada'` (0 'pendiente', ≥1 'pagada').
 *
 * Selección por PAGO (el usuario tilda pagos, no activos): un pago cuyo bancario paga cuotas de un
 * activo NO candidato (parcial, ya facturado, o eliminado) queda FUERA — evita listar un pago "a
 * caballo" entre facturas. NO filtra por proveedor: los activos de CapEx nacen con `proveedor_id`
 * null (el modal Inversión no lo captura hoy), así que el proveedor lo pone la asociación, no el dato.
 * NO hay auto-matching (regla 10.4): devuelve el universo candidato; el usuario elige cuáles son de
 * su factura por monto y contexto.
 *
 * @param {string} pais - "Chile" | "Perú" (columna `movimientos_caja.pais`).
 * @returns {Promise<Array<{
 *   movimientoCajaId: number,
 *   movimientoBancarioId: number,
 *   fecha: string,
 *   monto: number,
 *   moneda: string,
 *   descripcion: string,
 *   contexto: Array<{ activoId: number, tipoActivo: string, edificioId: number|null, edificioNombre: string }>,
 *   activoIds: number[],
 * }>>} pagos candidatos, ordenados por fecha ascendente.
 */
export const getPagosCapexCandidatos = async (pais) => {
  if (!pais) return [];

  // 1. Pagos CapEx del país (una fila de caja por transferencia). estado<>'anulado' + banco poblado.
  const { data: pagosRaw, error: errPagos } = await client
    .from("movimientos_caja")
    .select("id, movimiento_bancario_id, fecha, monto, moneda, descripcion")
    .eq("pais", pais)
    .eq("categoria_pago", "compra_activo")
    .neq("estado", "anulado")
    .not("movimiento_bancario_id", "is", null);
  if (errPagos) throw errPagos;
  const pagos = pagosRaw || [];
  if (pagos.length === 0) return [];

  const bancarioIds = [...new Set(pagos.map((p) => p.movimiento_bancario_id))];

  // 2. Candado (1): bancarios con fila MBF → ya vinculados a una factura → se descartan.
  const { data: mbfRows, error: errMbf } = await client
    .from("movimiento_bancario_facturas")
    .select("movimiento_bancario_id")
    .in("movimiento_bancario_id", bancarioIds);
  if (errMbf) throw errMbf;
  const bancariosConMBF = new Set((mbfRows || []).map((m) => m.movimiento_bancario_id));

  // 3. Cuotas PAGADAS de esos bancarios → qué activos paga cada bancario.
  const { data: cuotasPagadas, error: errCuotas } = await client
    .from("plan_pagos_activos")
    .select("activo_id, movimiento_bancario_id")
    .eq("estado", "pagada")
    .in("movimiento_bancario_id", bancarioIds);
  if (errCuotas) throw errCuotas;
  const activosPorBancario = new Map(); // movimiento_bancario_id → Set(activo_id)
  const activoIds = new Set();
  for (const c of cuotasPagadas || []) {
    if (!activosPorBancario.has(c.movimiento_bancario_id)) activosPorBancario.set(c.movimiento_bancario_id, new Set());
    activosPorBancario.get(c.movimiento_bancario_id).add(c.activo_id);
    activoIds.add(c.activo_id);
  }
  if (activoIds.size === 0) return [];
  const activoIdsArr = [...activoIds];

  // 4. TODAS las cuotas de esos activos (cualquier estado) → para exigir 0 'pendiente' por activo.
  const { data: cuotasTodas, error: errTodas } = await client
    .from("plan_pagos_activos")
    .select("activo_id, estado")
    .in("activo_id", activoIdsArr);
  if (errTodas) throw errTodas;
  const cuotasPorActivo = new Map(); // activo_id → { pagadas, pendientes }
  for (const c of cuotasTodas || []) {
    const acc = cuotasPorActivo.get(c.activo_id) || { pagadas: 0, pendientes: 0 };
    if (c.estado === "pagada") acc.pagadas += 1;
    else if (c.estado === "pendiente") acc.pendientes += 1;
    cuotasPorActivo.set(c.activo_id, acc);
  }

  // 5. Datos de los activos (estado / factura / eliminado / destino / tipo).
  const { data: activosRows, error: errAct } = await client
    .from("activos")
    .select("id, estado, factura_sii_recibida_id, eliminado, destino_tipo, destino_id, tipo_activo")
    .in("id", activoIdsArr);
  if (errAct) throw errAct;
  const activoPorId = new Map((activosRows || []).map((a) => [a.id, a]));

  // 6. Nombres de edificio para el contexto "qué compró".
  const edificioIds = [
    ...new Set(
      (activosRows || []).filter((a) => a.destino_tipo === "edificio" && a.destino_id != null).map((a) => a.destino_id)
    ),
  ];
  const nombrePorEdificio = new Map();
  if (edificioIds.length > 0) {
    const { data: edificios, error: errEd } = await client
      .from("edificios")
      .select("id, edificio")
      .in("id", edificioIds);
    if (errEd) throw errEd;
    for (const e of edificios || []) nombrePorEdificio.set(e.id, e.edificio);
  }

  // Candado (2): un activo es candidato si planificado + sin factura + no eliminado + todas sus
  // cuotas pagadas (0 pendientes, ≥1 pagada).
  const esActivoCandidato = (activoId) => {
    const a = activoPorId.get(activoId);
    if (!a) return false;
    if (a.estado !== "planificado" || a.factura_sii_recibida_id != null || a.eliminado) return false;
    const c = cuotasPorActivo.get(activoId);
    return !!c && c.pendientes === 0 && c.pagadas >= 1;
  };

  // Un PAGO es candidato si su bancario no tiene MBF y TODOS los activos que paga son candidatos.
  const candidatos = [];
  for (const p of pagos) {
    if (bancariosConMBF.has(p.movimiento_bancario_id)) continue;
    const activosDelBancario = [...(activosPorBancario.get(p.movimiento_bancario_id) || [])];
    if (activosDelBancario.length === 0) continue;
    if (!activosDelBancario.every(esActivoCandidato)) continue;
    candidatos.push({
      movimientoCajaId: p.id,
      movimientoBancarioId: p.movimiento_bancario_id,
      fecha: p.fecha,
      monto: Number(p.monto),
      moneda: p.moneda,
      descripcion: p.descripcion || "",
      activoIds: activosDelBancario,
      contexto: activosDelBancario.map((id) => {
        const a = activoPorId.get(id);
        return {
          activoId: id,
          tipoActivo: a?.tipo_activo || "—",
          edificioId: a?.destino_tipo === "edificio" ? a.destino_id : null,
          edificioNombre:
            a?.destino_tipo === "edificio" ? nombrePorEdificio.get(a.destino_id) || `Edificio #${a.destino_id}` : "—",
        };
      }),
    });
  }
  candidatos.sort((a, b) => (a.fecha < b.fecha ? -1 : a.fecha > b.fecha ? 1 : 0));
  return candidatos;
};
